'use client';

import { useLayoutEffect } from 'react';

const refreshShortcutKey = 'landCorp:refreshShortcut';
const savedScrollKey = 'landCorp:savedScrollY';

function restoreScrollTo(scrollY: number) {
  window.scrollTo(0, scrollY);
  requestAnimationFrame(() => window.scrollTo(0, scrollY));
  window.setTimeout(() => window.scrollTo(0, scrollY), 100);
}

function getNavigationType() {
  const navigationEntry = performance.getEntriesByType('navigation')[0] as
    PerformanceNavigationTiming | undefined;

  return navigationEntry?.type;
}

/**
 * Replaces a beforeInteractive inline script to avoid React 19 / Turbopack
 * "script tag while rendering" console warnings from next/script.
 */
export function ResetScrollOnRefresh() {
  useLayoutEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    const navigationType = getNavigationType();
    const savedScrollY = sessionStorage.getItem(savedScrollKey);
    const wasRefreshShortcut = sessionStorage.getItem(refreshShortcutKey) === 'true';

    sessionStorage.removeItem(refreshShortcutKey);
    sessionStorage.removeItem(savedScrollKey);

    if (
      navigationType === 'reload' &&
      process.env.NODE_ENV === 'development' &&
      !wasRefreshShortcut
    ) {
      restoreScrollTo(Number(savedScrollY ?? 0));
    } else if (navigationType === 'reload') {
      restoreScrollTo(0);
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const isRefreshShortcut =
        event.key === 'F5' || ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'r');

      if (isRefreshShortcut) {
        sessionStorage.setItem(refreshShortcutKey, 'true');
      }
    };

    const saveScrollPosition = () => {
      sessionStorage.setItem(savedScrollKey, String(window.scrollY));
    };

    saveScrollPosition();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', saveScrollPosition, { passive: true });
    window.addEventListener('pagehide', saveScrollPosition);
    window.addEventListener('beforeunload', saveScrollPosition);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', saveScrollPosition);
      window.removeEventListener('pagehide', saveScrollPosition);
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, []);

  return null;
}
